/**
 * @file firmware.ts
 * @description P5 PLUS / P6 PLUS Telink 底座固件升级 feature。
 *
 * 原厂 RapooSystem 确认：升级接口为 FFEF:00；打开后等待 3000ms，使用 Report
 * ID 0x05 的 Telink USB OTA 帧序列写入唯一 BIN，结束帧必须返回 status 0。
 */

import type { HidChannel, HidReportListener } from '../../../../channels/hid/HidChannel'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { FirmwareFeature } from '../../../../features/firmware/contract'
import { FirmwareEvents } from '../../../../events/FirmwareEvents'
import type { BaseDevice } from '../../../../runtime/BaseDevice'
import {
  DeviceType,
  type RemoteDeviceVersion,
  type VersionInfo,
  VersionType,
} from '../../../../types/firmware'
import type { BaseDockProfile } from '../../../shared/capabilities/base-command/profile'
import { processZip } from '../../../shared/firmware/shared/FileUtil'
import { sendBaseCommand } from '../../../shared/protocols/base-command/command-io'
import { delay } from '../../../../utils/TimeUtils'
import {
  parseTelinkKmUsbOtaFinalStatus,
  TELINK_KM_USB_OTA_HID,
  TelinkKmUsbOtaHostSession,
} from '../../protocols/mouse-usb-ota'
import { parseTelinkBaseOtaBin, type TelinkBaseOtaBin } from '../../protocols/base-usb-ota'

const VERSION_FILE_NAME = 'version.json'
const OTA_BOOT_WAIT_MS = 3000
const OTA_FRAME_TIMEOUT_MS = 5000

type TelinkBaseFirmwareVersionFile = {
  Device?: RemoteDeviceVersion[]
}

/** P5/P6 升级状态文字，供 UI 在无进度时显示。 */
type FirmwareUpdateStage = 'downloading' | 'downloaded' | 'bootloader' | 'writing' | 'written'

/** P5 PLUS / P6 PLUS 的已确认 Telink USB OTA 实现。 */
export class TelinkBaseFirmware extends FirmwareFeature {
  public constructor(
    device: BaseDevice,
    private readonly profile: BaseDockProfile,
  ) {
    super(device)
  }

  public async getFirmwareVersion(): Promise<VersionInfo[]> {
    const data = await sendBaseCommand(
      this.device,
      this.profile.config,
      this.profile.commands.get_firmware,
    )
    return [
      {
        version: `${data.getUint8(4)}.${data.getUint8(3)}.${data.getUint8(2)}.${data.getUint8(1)}`,
        versionType: VersionType.Firmware,
        deviceType: DeviceType.Base,
      },
    ]
  }

  public async checkFirmwareUpdate(): Promise<VersionInfo[]> {
    const [versions, release] = await Promise.all([
      this.getFirmwareVersion(),
      this.getVersionInfo(),
    ])
    return versions.map((version) => ({
      ...version,
      canUpdate: this.canUpdateByVersion(release.FirmwareVersion, version.version),
      ...(release.FirmwareVersion ? { onlineVersion: release.FirmwareVersion } : {}),
    }))
  }

  public async updateFirmware(_report?: () => void): Promise<void> {
    const versions = await this.checkFirmwareUpdate()
    if (!versions.some((version) => version.canUpdate)) return

    const release = await this.getVersionInfo()
    if (!release.FirmwareURL) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'P5/P6 固件下载地址为空')
    }

    this.reportStage(
      'downloading',
      `正在下载固件包（线上版本：${release.FirmwareVersion ?? '未提供'}）`,
    )
    const bin = this.parseOtaBin(await this.downloadFirmware(release.FirmwareURL))
    this.reportStage('downloaded', `固件包校验完成（BIN：${bin.size} 字节）`)

    await this.device.runAtomic(async () => {
      const channel = await this.device.session.channels.openChannel({ usageType: 'update' })
      if (!channel) {
        throw new DeviceError(
          DeviceErrorCode.NOT_SUPPORTED_VERSION,
          '未找到 P5/P6 固件升级接口 FFEF:00',
        )
      }
      try {
        this.reportStage('bootloader', '正在等待 Telink Bootloader 就绪')
        await delay(OTA_BOOT_WAIT_MS)
        this.reportStage('writing', 'Bootloader 已就绪，正在写入固件', 0)
        await this.writeFirmware(channel, bin)
      } finally {
        await this.releaseUpdateChannel(channel)
      }
    })

    this.reportStage('written', '固件写入完成，设备将自行重启', 100)
  }

  private async getVersionInfo(): Promise<RemoteDeviceVersion> {
    const response = await fetch(
      `${this.device.getFirmwareBaseUrl()}/${encodeURIComponent(this.device.manifest.mainControl)}/${VERSION_FILE_NAME}?t=${Date.now()}`,
      { referrerPolicy: 'no-referrer' },
    )
    if (!response.ok) {
      throw new DeviceError(DeviceErrorCode.TIMEOUT, `读取 P5/P6 在线版本失败：${response.status}`)
    }
    const data = (await response.json()) as TelinkBaseFirmwareVersionFile
    const release = data.Device?.[0]
    if (!release) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'P5/P6 在线版本文件缺少 Device 配置')
    }
    return release
  }

  private async downloadFirmware(fileName: string): Promise<Uint8Array> {
    const model = this.device.manifest.mainControl
    const files = await processZip(
      `${this.device.getFirmwareBaseUrl()}/${encodeURIComponent(model)}/${encodeURIComponent(fileName)}`,
      { referrerPolicy: 'no-referrer' },
    )
    const entries = Object.entries(files)
    const rootEntries = entries.filter(([path]) => !path.includes('/'))
    const appEntries = entries.filter(([path]) => /^app\/[^/]+$/i.test(path))
    const candidate = this.findOnlyBin(rootEntries) ?? this.findOnlyBin(appEntries)
    if (!candidate) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'P5/P6 固件包必须在根目录或 app/ 目录中包含唯一 BIN 文件',
      )
    }
    return candidate
  }

  private findOnlyBin(
    entries: [string, { extension: string; data: Uint8Array }][],
  ): Uint8Array | null {
    if (entries.length !== 1) return null
    const [, entry] = entries[0]!
    return entry.extension === '.bin' ? entry.data : null
  }

  private parseOtaBin(file: Uint8Array): TelinkBaseOtaBin {
    try {
      return parseTelinkBaseOtaBin(file)
    } catch (error) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        error instanceof Error ? error.message : String(error),
      )
    }
  }

  private async writeFirmware(channel: HidChannel, bin: TelinkBaseOtaBin): Promise<void> {
    const session = new TelinkKmUsbOtaHostSession(bin)
    for (;;) {
      const frame = session.next()
      if (!frame) return
      const response = await this.exchangeOtaFrame(channel, frame.payload)
      const status = parseTelinkKmUsbOtaFinalStatus(response)
      this.reportProgress(frame.percent)
      if (status !== undefined && status !== 0) {
        throw new DeviceError(DeviceErrorCode.WRITE_FAILED, `P5/P6 OTA 失败：status=${status}`)
      }
      if (frame.phase === 'end' && status !== 0) {
        throw new DeviceError(DeviceErrorCode.WRITE_FAILED, 'P5/P6 结束帧未收到 status==0')
      }
    }
  }

  private async exchangeOtaFrame(channel: HidChannel, payload: Uint8Array): Promise<DataView> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup()
        reject(new DeviceError(DeviceErrorCode.TIMEOUT, '等待 P5/P6 Bootloader Input Report 超时'))
      }, OTA_FRAME_TIMEOUT_MS)
      const listener: HidReportListener = (_reportId, data) => {
        cleanup()
        resolve(data)
      }
      const cleanup = () => {
        clearTimeout(timer)
        channel.offInputReport(listener)
      }
      channel.onInputReport(listener)
      channel.sendOutputReport(TELINK_KM_USB_OTA_HID.reportId, payload).catch((error) => {
        cleanup()
        reject(error)
      })
    })
  }

  private async releaseUpdateChannel(channel: HidChannel): Promise<void> {
    if (channel.nativeDevice === this.device.channel.nativeDevice) {
      this.device.session.channels.unregister({ usageType: 'update' })
      return
    }
    await this.device.session.channels.close({ usageType: 'update' })
  }

  private reportProgress(progress: number): void {
    this.device.emit(FirmwareEvents.WritedData, {
      progress: Math.floor(Math.min(100, Math.max(0, progress))),
    })
  }

  private reportStage(stage: FirmwareUpdateStage, message: string, progress?: number): void {
    this.device.emit(FirmwareEvents.Progress, { stage, message, progress })
  }
}
